import { useParams } from "react-router-dom";
import { Button, LoadingSpinner, Notice, Section } from "../../../components";
import { useAnimate, useStatsigClient } from "../../../hooks";
import {
  useCreateLead,
  useGetLeads,
  useGetOrganization,
} from "../../../services";
import { ExistingLeadRow } from "./components/ExistingLeadRow";
import { MobileMenuDrawer } from "./components/MobileMenuDrawer";
import { LinkCreatorDrawer } from "./components/link-creator/LinkCreatorDrawer";
import { AssignmentDrawer } from "./components/assignment-drawer";
import { MatrixDrawer } from "./components/matrix-drawer/MatrixDrawer";
import React, { Suspense, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ArchivedLeads } from "./components/ArchivedLeads";
import { Loading } from "../layout/components/Loading";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faUsers,
  faUserCheck,
  faUserTimes,
  faFingerprint,
  faRainbow,
} from "@fortawesome/free-solid-svg-icons";
import { homeState } from "../../../state/home";
import { discoverAndSubscribeAll, useMatchingState, clearNeedsMatch, runSemanticMatchStream } from "../../../services/organtization/semantic-match";
import { SkillStackDrawer } from "./components/skills/SkillStackDrawer";
import { Lead } from "../../../client";
import { jsonParse } from "../../../services/utils/parse";
import { DropdownButton } from "../../../components/buttons/dropdown-button";
import { SmartPopoverMenu, Image } from "../../../components";
import { cn } from "../../../components/utils/cn";
import { getImageUrl } from "../../../utils/image";
import AddNewRow from "./components/AddNewRow";
import { SectionCard } from "../../../components/section/SectionCard";
import { OrganizationProfileProgress } from "./components/onboarding/OrganizationProfileProgress";
import { ImportCVsSectionCard } from "./components/onboarding/ImportCVsSectionCard";
import { OrganizationInfoDrawer } from "./components/onboarding/OrganizationInfoDrawer";
import { ContactDetailsDrawer } from "./components/onboarding/ContactDetailsDrawer";
import * as Dialog from "@radix-ui/react-dialog";
import logoPrimaryBlack from "../../../assets/logo_primary_black.svg";
import { requestUpgrade } from "../../../requests";
import { WeeklySectionCard } from "./components/onboarding/WeeklySectionCard";
import { useSnapshot } from "valtio";
import { dialogsState } from "../../../state/dialogs";
import { WordExportInfoDialog } from "./components/WordExportInfoDialog";

const SkillStackSection = React.lazy(
  () => import("../home/components/SkillStackSection"),
);

const blankLead: Lead = {
  id: "",
  clientId: "",
  title: "",
  description: "",
  contactPersonName: "",
  contactPersonEmail: "",
  contactPersonPhone: "",
  contractId: "",
  status: "",
  numberOfUnreadEvents: 0,
  // Will always get overridden before being sent to the backend
  orgId: "",
  profileVersions: [],
};

const Component = () => {
  const { organizationId } = useParams();
  if (!organizationId) throw new Error("Organization ID is required");
  const [shouldFetch, setShouldFetch] = useState(false);
  const [isCreatingLead, setIsCreatingLead] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [localNewLeadCounter, setLocalNewLeadCounter] = useState(0);
  const [visibleCount, setVisibleCount] = useState(30);

  enum AssignmentType {
    ALL = "all",
    ASSIGNED = "assigned",
    UNASSIGNED = "unassigned",
  }

  enum CreatedByType {
    EVERYONE = "everyone",
  }

  enum SortOrder {
    NEWEST_ON_TOP = "newest_on_top",
    OLDEST_ON_TOP = "oldest_on_top",
  }

  // Initialize state with defaults
  const [selectedAssignmentType, setSelectedAssignmentType] =
    useState<AssignmentType>(AssignmentType.ALL);

  const [selectedCreatedBy, setSelectedCreatedBy] = useState<
    CreatedByType | string
  >(CreatedByType.EVERYONE);

  const [selectedSortOrder, setSelectedSortOrder] = useState<SortOrder>(
    SortOrder.NEWEST_ON_TOP,
  );

  const { data, isFetching: isLeadsFetching } = useGetLeads(organizationId);
  const { data: orgData } = useGetOrganization(organizationId);
  const queryClient = useQueryClient();

  useEffect(() => {
    const navStart = Number(sessionStorage.getItem("dashboard-nav-start") ?? 0);
    const elapsed = navStart ? (performance.now() - navStart).toFixed(0) : "?";
    console.log(
      `[HOME] Mounted — leads: ${data?.length ?? 0}, org: ${orgData?.name ?? "?"}, ${elapsed}ms since navigation`,
    );
  }, []);

  const statsigClient = useStatsigClient();
  const isGroupedLeads =
    statsigClient?.checkGate("semantic_match_layout") ?? false;

  const { showWordExportInfo } = useSnapshot(dialogsState);

  useEffect(() => {
    if (!homeState.organizationIconName) {
      homeState.organizationIconName = orgData.iconLogoId
        ? orgData.iconLogoId
        : null;
    }
  }, [orgData]);

  // Discover and subscribe to any active match jobs when the leads list loads
  useEffect(() => {
    if (!data) return;
    discoverAndSubscribeAll(organizationId, queryClient);
  }, [organizationId, data, queryClient]);

  // Poll while any lead is in the "parsing" phase so we detect when it
  // transitions to matching and can open the SSE stream automatically.
  const matchingState = useMatchingState();
  const hasParsingLeads = Object.values(matchingState.phase).some(
    (p) => p === "parsing",
  );
  useEffect(() => {
    if (!hasParsingLeads) return;
    const interval = setInterval(() => {
      discoverAndSubscribeAll(organizationId, queryClient);
    }, 3000);
    return () => clearInterval(interval);
  }, [hasParsingLeads, organizationId, queryClient]);

  // After a page reload mid-parse, discoverAndSubscribeAll marks leads whose
  // parsing completed while away as needsMatch. Auto-trigger matching for them.
  const needsMatchIds = Object.keys(matchingState.needsMatch);
  useEffect(() => {
    if (needsMatchIds.length === 0) return;
    for (const leadId of needsMatchIds) {
      clearNeedsMatch(leadId);
      runSemanticMatchStream(organizationId, leadId, queryClient);
    }
  }, [needsMatchIds.join(","), organizationId, queryClient]);

  // Reset visible count when filters/sort change
  useEffect(() => {
    setVisibleCount(30);
  }, [selectedAssignmentType, selectedCreatedBy, selectedSortOrder]);


  const createLead = useCreateLead({
    onSuccess: (lead) => {
      const newLead = jsonParse<Lead>(lead);
      if (newLead.id) {
        homeState.selectedLead = { leadId: newLead.id, state: "assignment" };
      }
    },
  });

  const [parent] = useAnimate();

  // Handle lead creation flow
  useEffect(() => {
    if (createLead.isPending) {
      setIsCreatingLead(true);
    } else if (isCreatingLead && !isLeadsFetching) {
      // Lead created and fetch completed
      setIsCreatingLead(false);
    }
  }, [createLead.isPending, isLeadsFetching, isCreatingLead]);

  if (!data) return <p>No data</p>;

  const handleAssignmentTypeChange = (newType: AssignmentType) => {
    setSelectedAssignmentType(newType);
  };

  const handleCreatedByChange = (newCreatedBy: CreatedByType | string) => {
    setSelectedCreatedBy(newCreatedBy);
  };

  const handleSortOrderChange = (newSortOrder: SortOrder) => {
    setSelectedSortOrder(newSortOrder);
  };

  const assignmentTypeDisplayName = (
    assignmentType: AssignmentType,
  ): JSX.Element => {
    const activeLeads = data.filter(
      (l) => !l.contractId && (isGroupedLeads ? !l.parentLeadId : true),
    );
    switch (assignmentType) {
      case AssignmentType.ALL:
        return <>All Leads ( {activeLeads.length} )</>;
      case AssignmentType.ASSIGNED:
        return (
          <>
            Assigned Leads{" "}
            <span className="text-red-3">
              (
              {isGroupedLeads
                ? activeLeads.filter(
                    // New-style: has child leads. Old-style: has profileId directly.
                    (l) =>
                      (childrenByParent.get(l.id ?? "") ?? []).length > 0 ||
                      !!l.profileId,
                  ).length
                : activeLeads.filter((lead) => lead.profileId).length}
              )
            </span>
          </>
        );
      case AssignmentType.UNASSIGNED:
        return (
          <>
            Unassigned Leads{" "}
            <span className="text-red-3">
              (
              {isGroupedLeads
                ? activeLeads.filter(
                    // Only truly unassigned if no children and no direct profileId
                    (l) =>
                      (childrenByParent.get(l.id ?? "") ?? []).length === 0 &&
                      !l.profileId,
                  ).length
                : activeLeads.filter((lead) => !lead.profileId).length}
              )
            </span>
          </>
        );
      default:
        return <>Unknown</>;
    }
  };

  const createdByDisplayName = (
    createdBy: CreatedByType | string,
  ): JSX.Element => {
    if (createdBy === CreatedByType.EVERYONE) {
      const activeLeads = data.filter((l) => !l.contractId);
      return <>Created by: Everyone ( {activeLeads.length} )</>;
    }
    const manager = orgData?.profiles?.find(
      (profile) => profile.profileId === createdBy && profile.isManager,
    );
    if (manager) {
      const displayName =
        (manager.profileData as any)?.displayName ||
        (manager.profileData as any)?.name ||
        "Unknown Manager";
      const count = data.filter(
        (l) => !l.contractId && l.createdBy === createdBy,
      ).length;
      return (
        <>
          Created by: {displayName}{" "}
          <span className="text-red-3">( {count} )</span>
        </>
      );
    }
    return <>Created by: Unknown</>;
  };

  const sortLeads = (leads: any[]) => {
    return [...leads].sort((a, b) => {
      if (selectedSortOrder === SortOrder.NEWEST_ON_TOP) {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA; // Newest first
      } else if (selectedSortOrder === SortOrder.OLDEST_ON_TOP) {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateA - dateB; // Oldest first
      }
      return 0;
    });
  };

  // In grouped mode: build a map of parentLeadId → child leads
  const childrenByParent = new Map<string, Lead[]>();
  if (isGroupedLeads) {
    data
      .filter((l) => !l.contractId && !!l.parentLeadId)
      .forEach((child) => {
        const siblings = childrenByParent.get(child.parentLeadId!) ?? [];
        childrenByParent.set(child.parentLeadId!, [child, ...siblings]);
      });
  }

  const filteredLeads = sortLeads(
    data
      .filter((l) => !l.contractId)
      // In grouped mode, only show parent leads (no parentLeadId) at the top level
      .filter((l) => !isGroupedLeads || !l.parentLeadId)
      .filter((lead) => {
        if (selectedAssignmentType === AssignmentType.ALL) return true;
        if (isGroupedLeads) {
          const children = childrenByParent.get(lead.id ?? "") ?? [];
          if (selectedAssignmentType === AssignmentType.ASSIGNED)
            return children.length > 0 || !!lead.profileId;
          if (selectedAssignmentType === AssignmentType.UNASSIGNED)
            return children.length === 0 && !lead.profileId;
        } else {
          if (selectedAssignmentType === AssignmentType.ASSIGNED)
            return !!lead.profileId;
          if (selectedAssignmentType === AssignmentType.UNASSIGNED)
            return !lead.profileId;
        }
        return true;
      })
      .filter((lead) => {
        if (selectedCreatedBy === CreatedByType.EVERYONE) return true;
        return lead.createdBy === selectedCreatedBy;
      }),
  );

  return (
    <>
    <AssignmentDrawer>
      <LinkCreatorDrawer>
          <OrganizationInfoDrawer>
            <ContactDetailsDrawer>
              <SkillStackDrawer>
                <MobileMenuDrawer>
                  {orgData?.profileLimit !== null &&
                  orgData?.profileLimit !== undefined ? (
                    <div className="flex flex-col lg:flex-row gap-10 items-stretch mb-10">
                      <ImportCVsSectionCard
                        organizationId={organizationId}
                        profiles={orgData.profiles.filter(
                          (p) => p.role !== "default",
                        )}
                        onUpgradeClick={() => {
                          setShowUpgradeDialog(true);
                        }}
                        onAddProfileClick={() => {}}
                      />
                      <SectionCard
                        title="Brand & style"
                        wrapperClassName="w-full lg:w-1/3 h-full"
                        className="h-full w-full"
                        icon={
                          <FontAwesomeIcon
                            icon={faRainbow}
                            className="text-gray-700 w-8 h-8"
                          />
                        }
                      >
                        {orgData && (
                          <OrganizationProfileProgress
                            organization={orgData}
                            showDefaultLeadSettings={
                              statsigClient?.checkGate(
                                "default_lead_settings",
                              ) ?? false
                            }
                          />
                        )}
                      </SectionCard>
                    </div>
                  ) : (
                    <div className="flex flex-col lg:flex-row gap-10 items-stretch mb-10">
                      <WeeklySectionCard
                        organizationId={organizationId}
                        profiles={orgData.profiles.filter(
                          (p) => p.role !== "default",
                        )}
                        newWeeklyProfiles={orgData.newWeeklyProfiles}
                        newWeeklyProfileLeads={
                          (orgData.newWeeklyProfileLeads || 0) +
                          localNewLeadCounter
                        }
                        newWeeklyLeads={orgData.newWeeklyLeads}
                        newWeeklyProfileVersions={orgData.newWeeklyProfileVersions}
                      />
                      <SectionCard
                        title="Brand & style"
                        wrapperClassName="w-full lg:w-1/3 h-full"
                        className="h-full w-full"
                        icon={
                          <FontAwesomeIcon
                            icon={faRainbow}
                            className="text-gray-700 w-8 h-8"
                          />
                        }
                      >
                        {orgData && (
                          <OrganizationProfileProgress
                            organization={orgData}
                            showDefaultLeadSettings={
                              statsigClient?.checkGate(
                                "default_lead_settings",
                              ) ?? false
                            }
                          />
                        )}
                      </SectionCard>
                    </div>
                  )}

                  <SectionCard
                    title="Leads"
                    subtitle="Make your Profiles client-ready. Automatically optimized and enriched for every new lead."
                    className="lg:overflow-x-scroll"
                    containerComponent="ul"
                    ref={parent}
                  >
                    <div className="flex flex-row gap-4 justify-between">
                      <div className="flex flex-row gap-4">
                        <DropdownButton
                          variant="secondary"
                          label={assignmentTypeDisplayName(
                            selectedAssignmentType,
                          )}
                        >
                          <>
                            <LeadFilterMenuRow
                              icon={faUsers}
                              text={`All Leads ( ${data.filter((l) => !l.contractId && (isGroupedLeads ? !l.parentLeadId : true)).length} )`}
                              selected={
                                selectedAssignmentType === AssignmentType.ALL
                              }
                              onClick={() =>
                                handleAssignmentTypeChange(AssignmentType.ALL)
                              }
                            />
                            <LeadFilterMenuRow
                              icon={faUserCheck}
                              text={`Assigned Leads ( ${isGroupedLeads ? data.filter((l) => !l.contractId && !l.parentLeadId && (childrenByParent.get(l.id ?? "") ?? []).length > 0).length : data.filter((l) => !l.contractId && l.profileId).length} )`}
                              selected={
                                selectedAssignmentType ===
                                AssignmentType.ASSIGNED
                              }
                              onClick={() =>
                                handleAssignmentTypeChange(
                                  AssignmentType.ASSIGNED,
                                )
                              }
                            />
                            <LeadFilterMenuRow
                              icon={faUserTimes}
                              text={`Unassigned Leads ( ${isGroupedLeads ? data.filter((l) => !l.contractId && !l.parentLeadId && (childrenByParent.get(l.id ?? "") ?? []).length === 0).length : data.filter((l) => !l.contractId && !l.profileId).length} )`}
                              selected={
                                selectedAssignmentType ===
                                AssignmentType.UNASSIGNED
                              }
                              onClick={() =>
                                handleAssignmentTypeChange(
                                  AssignmentType.UNASSIGNED,
                                )
                              }
                            />
                          </>
                        </DropdownButton>
                        <DropdownButton
                          variant="secondary"
                          label={createdByDisplayName(selectedCreatedBy)}
                        >
                          <>
                            <LeadFilterMenuRow
                              icon={faUsers}
                              text={`Everyone ( ${data.filter((l) => !l.contractId).length} )`}
                              selected={
                                selectedCreatedBy === CreatedByType.EVERYONE
                              }
                              onClick={() =>
                                handleCreatedByChange(CreatedByType.EVERYONE)
                              }
                            />
                            {orgData?.profiles
                              ?.filter((profile) => profile.isManager)
                              ?.map((manager) => {
                                const displayName =
                                  (manager.profileData as any)?.displayName ||
                                  (manager.profileData as any)?.name ||
                                  "Unknown Manager";
                                const count = data.filter(
                                  (l) =>
                                    !l.contractId &&
                                    l.createdBy === manager.profileId,
                                ).length;
                                return (
                                  <LeadFilterMenuRow
                                    key={manager.profileId}
                                    profilePhotoId={manager.profilePhotoId}
                                    profileName={displayName}
                                    text={`${displayName} ( ${count} )`}
                                    selected={
                                      selectedCreatedBy === manager.profileId
                                    }
                                    onClick={() =>
                                      handleCreatedByChange(manager.profileId)
                                    }
                                  />
                                );
                              }) || []}
                          </>
                        </DropdownButton>
                      </div>
                      <DropdownButton
                        variant="secondary"
                        label={
                          selectedSortOrder === SortOrder.NEWEST_ON_TOP
                            ? "Newest on top"
                            : "Oldest on top"
                        }
                      >
                        <>
                          <LeadFilterMenuRow
                            text="Newest on top"
                            selected={
                              selectedSortOrder === SortOrder.NEWEST_ON_TOP
                            }
                            onClick={() =>
                              handleSortOrderChange(SortOrder.NEWEST_ON_TOP)
                            }
                          />
                          <LeadFilterMenuRow
                            text="Oldest on top"
                            selected={
                              selectedSortOrder === SortOrder.OLDEST_ON_TOP
                            }
                            onClick={() =>
                              handleSortOrderChange(SortOrder.OLDEST_ON_TOP)
                            }
                          />
                        </>
                      </DropdownButton>
                    </div>
                    <AddNewRow
                      isPending={isCreatingLead}
                      tooltipText="Add new lead"
                      onClick={() => {
                        setLocalNewLeadCounter(localNewLeadCounter + 1);
                        createLead.mutate({
                          body: {
                            ...blankLead,
                            orgId: organizationId,
                          },
                          path: {
                            organizationID: organizationId,
                          },
                        });
                      }}
                    />

                    {filteredLeads
                      .filter((l) => l.id !== orgData.defaultLeadId)
                      .slice(0, visibleCount)
                      .map((lead) => (
                        <ExistingLeadRow
                          key={lead.id}
                          organizationId={organizationId}
                          profiles={orgData.profiles}
                          lead={lead}
                          childLeads={
                            // Only enable grouped UI for true assignment leads (no profileId).
                            // Old-style leads (profileId set, no parentLeadId) fall back to
                            // the old single-profile UI during the transition period.
                            isGroupedLeads && !lead.profileId
                              ? (childrenByParent.get(lead.id) ?? [])
                              : undefined
                          }
                        />
                      ))}
                    {filteredLeads.filter((l) => l.id !== orgData.defaultLeadId).length > visibleCount && (
                      <div className="flex justify-center pt-2">
                        <Button variant="secondary" onClick={() => setVisibleCount((c) => c + 30)}>
                          Load more
                        </Button>
                      </div>
                    )}
                  </SectionCard>

                  <Notice
                    className="bg-transparent"
                    label="Contracts can be found under each profiles page"
                  />

                  {shouldFetch ? (
                    <div>
                      <div className="flex flex-1 w-full justify-center items-center">
                        <Button onClick={() => setShouldFetch(false)}>
                          Hide archived leads
                        </Button>
                      </div>
                      <div>
                        <Suspense fallback={<Loading />}>
                          <ArchivedLeads organizationId={organizationId} />
                        </Suspense>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-1 w-full justify-center items-center pb-20">
                      <Button onClick={() => setShouldFetch(true)}>
                        Show archived leads
                      </Button>
                    </div>
                  )}

                  <Dialog.Root open={showUpgradeDialog}>
                    <Dialog.Portal>
                      <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50 z-50" />
                      <Dialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-red-1 rounded-2xl p-10 shadow-lg z-50 max-w-md w-full mx-4">
                        <div className="flex flex-col justify-center items-center">
                          <img
                            src={logoPrimaryBlack}
                            alt="Logo"
                            className="w-38 h-38"
                          />
                          <h2 className="text-lg font-extrabold text-black mt-10">
                            Ready for the next level?
                          </h2>

                          <Dialog.Description className="text-sm text-gray-6 mt-4 text-center mb-10">
                            Upgrade to VOC Studio Pro and
                            <span className="font-bold">
                              unlock unlimited number of profiles
                            </span>
                            and manage all your employees, subcontractors, and
                            cadidates in one place. Use the button below and
                            we'll get in touch with you as soon as possible!
                          </Dialog.Description>

                          <Button
                            variant="primary"
                            onClick={() => {
                              requestUpgrade();
                              setShowUpgradeDialog(false);
                            }}
                          >
                            Request to upgrade
                          </Button>
                          <Button
                            variant="secondary"
                            className="mt-4"
                            onClick={() => {
                              setShowUpgradeDialog(false);
                            }}
                          >
                            Close
                          </Button>
                        </div>
                      </Dialog.Content>
                    </Dialog.Portal>
                  </Dialog.Root>
                  <WordExportInfoDialog open={showWordExportInfo} />
                </MobileMenuDrawer>
              </SkillStackDrawer>
            </ContactDetailsDrawer>
          </OrganizationInfoDrawer>
      </LinkCreatorDrawer>
    </AssignmentDrawer>
    <MatrixDrawer />
    </>
  );
};

Component.displayName = "DashboardHome";

const LeadFilterMenuRow = ({
  icon,
  text,
  onClick,
  iconClassName,
  selected,
  profilePhotoId,
  profileName,
}: LeadFilterMenuRowProps) => (
  <SmartPopoverMenu.Item onClick={onClick}>
    {selected && (
      <FontAwesomeIcon
        width={14}
        height={14}
        icon={faCheck}
        className="text-red-3 mr-2"
      />
    )}
    {profilePhotoId ? (
      <Image
        src={getImageUrl(profilePhotoId)}
        alt={profileName || "Profile"}
        className="rounded-xl w-6 h-6 flex-shrink-0 mr-2"
      />
    ) : icon ? (
      <FontAwesomeIcon
        width={14}
        height={14}
        icon={icon}
        className={cn("shrink-0 mr-2", iconClassName)}
      />
    ) : null}
    <span className="text-sm text-black">{text}</span>
  </SmartPopoverMenu.Item>
);

interface LeadFilterMenuRowProps {
  onClick: () => void;
  icon?: any;
  text: string;
  iconClassName?: string;
  selected: boolean;
  profilePhotoId?: string | null;
  profileName?: string;
}

export default Component;
